import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  fetchAdminContent,
  saveAdminSection,
  getAdminToken,
  setAdminToken,
  isAdminSessionError,
} from '../content/contentApi';
import { normalizeAboutPage } from '../content/aboutPageTemplate';
import EditableAboutPage from '../components/about/EditableAboutPage';
import EditorTopBar from '../components/services/editable/EditorTopBar';
import './Admin.css';

/**
 * Dedicated full-viewport route for inline WYSIWYG editing of the (singleton)
 * About page — the About-page counterpart to ServicePageEditor.jsx. No
 * slug/list/create step since there's only ever one About page; it fetches
 * and saves the `aboutPage` section directly.
 */
export default function AboutPageEditor() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [draft, setDraft] = useState(null); // { data, styles }
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [preview, setPreview] = useState(false);
  const [status, setStatus] = useState('');

  const handleSessionExpired = useCallback(() => {
    setAdminToken(null);
    navigate('/admin/login?reason=session', { replace: true });
  }, [navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const api = await fetchAdminContent();
      const doc = normalizeAboutPage(api.aboutPage);
      setDraft({ data: doc.data, styles: doc.styles });
      setDirty(false);
    } catch (e) {
      if (isAdminSessionError(e)) {
        handleSessionExpired();
        return;
      }
      setError(e.message || 'Failed to load the About page.');
    } finally {
      setLoading(false);
    }
  }, [handleSessionExpired]);

  useEffect(() => {
    if (!getAdminToken()) {
      navigate('/admin/login', { replace: true });
      return;
    }
    load();
  }, [load, navigate]);

  const handleChange = (patch) => {
    setDraft((prev) => ({ ...prev, ...patch }));
    setDirty(true);
  };

  const handleSave = async () => {
    if (!draft) return;
    setSaving(true);
    setError('');
    try {
      const now = new Date().toISOString();
      const nextSection = { data: draft.data, styles: draft.styles, updatedAt: now };
      await saveAdminSection('aboutPage', nextSection);
      setDirty(false);
      setStatus('Saved · the public /about page now matches this.');
    } catch (e) {
      if (isAdminSessionError(e)) {
        handleSessionExpired();
        return;
      }
      setError(e.message || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !draft) {
    return (
      <div className="admin-root">
        <div className="admin-loading">
          <p>Loading page editor…</p>
        </div>
      </div>
    );
  }

  const statusLabel = saving ? 'Saving…' : dirty ? 'Unsaved changes' : status || 'No changes yet';

  return (
    <div className="admin-root">
      <EditorTopBar
        title="About Page"
        preview={preview}
        onTogglePreview={() => setPreview((p) => !p)}
        onSave={handleSave}
        saving={saving}
        statusLabel={statusLabel}
        onBack={() => navigate('/admin')}
      />
      {error ? <p className="admin-error" style={{ padding: '10px 20px', margin: 0 }}>{error}</p> : null}
      <EditableAboutPage data={draft.data} styles={draft.styles} editable preview={preview} onChange={handleChange} />
    </div>
  );
}
